import { useState } from 'react'

/**
 * Password field with the show/hide eye toggle.
 *
 * Ported from `Login.initPasswordToggle`, which every Account page repeated
 * against hardcoded element ids (`#togglePassword`, `#toggleConfirmPassword`),
 * so a page with a third password box would have needed a third copy. Same
 * markup and the same fa-eye / fa-eye-slash swap.
 *
 * The `::-ms-reveal` suppression was a <style> block in Login.cshtml, kept here
 * so Edge does not show a second, native eye icon next to this one.
 */
interface PasswordInputProps {
  id: string
  name?: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  required?: boolean
  autoComplete?: string
  className?: string
}

export function PasswordInput({
  id,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  autoComplete,
  className = 'form-control',
}: PasswordInputProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div className="input-group">
      <style>{`input[type="password"]::-ms-reveal, input[type="password"]::-ms-clear { display: none; }`}</style>
      <input
        type={isVisible ? 'text' : 'password'}
        className={className}
        id={id}
        name={name ?? id}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        autoComplete={autoComplete}
      />
      <button
        className="btn btn-outline-secondary"
        type="button"
        id={`toggle-${id}`}
        tabIndex={-1}
        onClick={() => setIsVisible((visible) => !visible)}
        aria-label={isVisible ? 'Hide password' : 'Show password'}
      >
        <i className={`fa ${isVisible ? 'fa-eye-slash' : 'fa-eye'}`} />
      </button>
    </div>
  )
}
